/* eslint-disable no-console */
import { Apollo } from 'apollo-angular';
import { DocumentNode, FieldNode, OperationDefinitionNode } from 'graphql';
import { catchError, OperatorFunction, throwError } from 'rxjs';

/**
 * Evicts a field from Apollo cache if present.
 * @param apollo Apollo instance
 * @param clientName Name of the Apollo client
 * @param fieldName Root field name to evict
 * @param args Optional field arguments
 * @param runGc Whether to run cache.gc() after eviction
 */
export function evictFromCacheOperator(
  apollo: Apollo,
  clientName: string,
  fieldName: string,
  args?: Record<string, any>,
  runGc = true,
): void {
  try {
    const client = clientName ? apollo.use(clientName).client : apollo.client;

    if (!client?.cache) {
      return;
    }

    const { cache } = client;
    const cacheData = cache.extract() as Record<
      string,
      Record<string, unknown>
    >;
    const rootQueryKey = 'ROOT_QUERY';

    if (rootQueryKey in cacheData) {
      const rootQuery = cacheData[rootQueryKey];

      const fieldKey = args
        ? `${fieldName}(${JSON.stringify(args)})`
        : fieldName;

      if (fieldName in rootQuery || fieldKey in rootQuery) {
        cache.evict({
          fieldName,
          args,
        });

        if (runGc) {
          cache.gc();
        }
      }
    }
  } catch (error) {
    throw new Error(`Error during cache eviction: ${error}`);
  }
}

export function getQueryRootFieldName(
  queryDocument: DocumentNode,
): string | null {
  const operationDef = queryDocument.definitions.find(
    (def) => def.kind === 'OperationDefinition' && def.operation === 'query',
  ) as OperationDefinitionNode | undefined;

  if (!operationDef || !operationDef.selectionSet.selections.length) {
    return null;
  }
  const firstSelection = operationDef.selectionSet.selections[0];

  if (firstSelection.kind !== 'Field') {
    return null;
  }

  return (firstSelection as FieldNode).name.value;
}

export function catchErrorAndEvict<T>(
  apollo: Apollo,
  clientName: string,
  fieldName: string,
): OperatorFunction<T, T> {
  return catchError((err) => {
    try {
      evictFromCacheOperator(apollo, clientName, fieldName);
    } catch (evictionError) {
      console.log(evictionError);
    }

    return throwError(() => err);
  });
}
